import { useSyncExternalStore } from 'react';

/**
 * Theme preference, persisted per device (HS-1533). The portal carries the same module
 * (`apps/portal/src/preferences/theme.ts`); the two are kept apart rather than shared because
 * each is one display preference with its own storage key, and a package for ~100 lines would
 * cost more than the duplication.
 *
 * `light` is the default: a first visit is light whatever the OS says. `system` is **not**
 * resolved to a concrete theme here — it removes the `data-theme` attribute and lets
 * `@smartsash/ui/tokens.css` do the work, where `:root { color-scheme: light dark }` makes every
 * `light-dark()` token follow `prefers-color-scheme`, live, with no `matchMedia` listener.
 *
 * The attribute goes on `<html>` rather than a React wrapper on purpose: `Tooltip` and
 * `DropdownMenu` portal their surfaces to `<body>` and recover the theme with
 * `closest('[data-theme]')`, which only reaches an ancestor of the *trigger*.
 */
export type ThemePreference = 'system' | 'light' | 'dark';

/** Every value the picker offers, in the order it presents them. */
export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

/** What an unset or unreadable preference falls back to. */
export const DEFAULT_THEME: ThemePreference = 'light';

/** `localStorage` key. Namespaced: admin and portal share a host in local development. */
export const THEME_STORAGE_KEY = 'admin-theme';

/** The DOM attribute `packages/ui/src/tokens/theme.css` keys its `color-scheme` overrides on. */
const THEME_ATTRIBUTE = 'data-theme';

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && THEME_PREFERENCES.includes(value as ThemePreference);
}

/**
 * The persisted preference, or the default. Anything unreadable — Safari private mode throwing
 * on access, a hand-edited value — resolves to the default rather than propagating an error out
 * of a display preference.
 */
export function readStoredTheme(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(raw) ? raw : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

/** Stamp (or clear, for `system`) `data-theme` on `<html>`. */
export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') {
    root.removeAttribute(THEME_ATTRIBUTE);
    return;
  }
  root.setAttribute(THEME_ATTRIBUTE, preference);
}

let current: ThemePreference = DEFAULT_THEME;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ThemePreference {
  return current;
}

/**
 * Read the persisted preference and apply it. Called from `main.tsx` **before** `createRoot`,
 * so a dark-theme user never sees a light first paint, and again by tests to reset the module.
 */
export function bootstrapTheme(): ThemePreference {
  current = readStoredTheme();
  applyTheme(current);
  emit();
  return current;
}

/** Change the theme: apply it, persist it, and wake every subscriber. */
export function setTheme(preference: ThemePreference): void {
  current = preference;
  applyTheme(preference);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // A preference that cannot be persisted still applies for this session.
  }
  emit();
}

/** The current preference, re-rendering the caller when it changes. */
export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
